import { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import FormikControl from "../../../components/common/FormikControl";
import { useDispatch, useSelector } from "react-redux";
import { addProductCalculation, editProductCalculation, selectProductCalculationLoading } from "../../../slices/productCalculations";
import { selectBmrAllProductList, fetchProductList } from "../../../slices/onBoardUser";
import { validationSchemaForProductMaster } from "../../../../utils/formik/validationSchema";
import { initialForProductMaster } from "../../../../utils/formik/initialValues"

const ProductCalculationsForm = ({ editData = null }) => {
    const allProductList = useSelector(selectBmrAllProductList)
    const dispatch = useDispatch()
    const isLoading = useSelector(selectProductCalculationLoading)
    const [initialValues, setInitialValues] = useState(initialForProductMaster)
    const [productOption, setProductOption] = useState([])
    
    useEffect(() => {
        dispatch(fetchProductList({type:'calculation'}))
    }, [dispatch])

    useEffect(() => {
        setInitialValues(prev => ({
            ...prev, ...editData, product_ids: editData?.products?.map(p => p.product_id) || []
        }));
    }, [editData])

    useEffect(() => {
        if (!allProductList?.length) return;

        const selectedProductIds = initialValues?.product_ids || [];

        const productList = allProductList
            .filter(product => {
                // EDIT case → allow selected products
                if (selectedProductIds.length) {
                    return (
                        selectedProductIds.includes(product.id) || // show selected
                        !product.is_mapped                         // show unused
                    );
                }
                // ADD case → only unused products
                return !product.is_mapped;
            })
            .map(product => ({
                value: product.id,
                label: product.name
            }));

        setProductOption(productList);
    }, [initialValues, allProductList]);

    const handleSubmit = async (values) => {
        if (editData) {
            await dispatch(editProductCalculation({ data: { ...values }, editId: editData.id }))
        } else {
            await dispatch(addProductCalculation({ ...values, active: values.active }))
        }
    }
    
    const formik = useFormik({
        initialValues,
        validationSchema: validationSchemaForProductMaster,
        onSubmit: handleSubmit,
        enableReinitialize: true
    })
    
    const formOptions = [
        {
            label: 'Product Name',
            type: 'select',
            name: 'product_ids',
            multiple: true,
            required: true,
            placeholder: "Select-",
            options: productOption
        },
        {
            label: 'Status',
            type: 'radio',
            name: 'active',
            required: true,
            defaultValue: true,
            options: [
                { value: true, label: "Active" },
                { value: false, label: "In-Active" },
            ]
        },
        {
            label: 'Description',
            type: 'textarea',
            name: 'description',
            required: true,
            placeholder: "Enter Description",
            multiline: true,
            rows: 3
        },
    ];

    const dateCalculations = [
        {
            field: "Expected Date Of LSM Received",
            reference: "Apheresis Collection",
            name: "lsm_received_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
            required: true,
        },
        {
            field: "Expected Date Of LSM Qualification",
            reference: "LSM Received(Actual)",
            name: "lsm_qualification_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
            required: true,
        },
        {
            field: "Manufacturing Date",
            reference: "LSM Qualification(Actual)",
            name: "manufacturing_completion_ed",
            type: "number",
            noText:true,
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "Manufacturing Date",
            manualField: "LSM Cryopreserve Scenario",
            reference: "LSM Qualification(Actual)",
            name: "cryo_manufacturing_date",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "Expected Date Of Completion Of Manufacturing",
            reference: "Manufacturing Date (Actual)",
            name: "manufacturing_completion_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "Expected Date Of Completion Of Manufacturing",
            manualField: "LSM Cryopreserve Scenario",
            reference: "Manufacturing Date (Actual)",
            name: "cryo_manufacturing_completion_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "Expected Date Of Completion QC Testing",
            reference: "Manufacturing Date (Actual)",
            name: "qc_testing_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "Expected Date Of Completion QC Testing",
            manualField: "LSM Cryopreserve Scenario",
            reference: "Manufacturing Date (Actual)",
            name: "cryo_qc_testing_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "(Expected date of completion) QA Review",
            reference: "Manufacturing Date (Actual)",
            name: "qa_review_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "Expected Date Of Completion QA Review",
            manualField: "LSM Cryopreserve Scenario",
            reference: "Manufacturing Date (Actual)",
            name: "cryo_qa_review_ed",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
        {
            field: "Expiry date",
            reference: "Manufacturing Date",
            name: "expiry_date",
            type: "number",
            notDecimal: true,
            placeholder: "Enter",
            subLable: "Days",
        },
    ];

    return (
        <Box className="w-full h-full flex flex-col">
            <form onSubmit={formik.handleSubmit} className="flex flex-col h-full">
                {/* Scrollable content area */}
                <Box className="flex-grow overflow-y-auto pr-2">
                    <Grid container spacing={2} className="mb-3">
                        {formOptions.map((props, key) => (
                            <Grid key={key} item xs={12}>
                                <FormikControl
                                    formik={formik}
                                    {...props}
                                    className="w-full"
                                />
                            </Grid>
                        ))}        
                        
                        <Grid item xs={12} className="w-full pl-3 mt-3">
                            <Grid
                                item xs={12}
                                className="flex items-center w-full gap-3 bg-[#F0F1F5] pb-1"
                            >
                                {/* Left Label */}
                                <Typography
                                    className="font-medium text-gray-800 px-3 py-2 rounded min-w-[420px] text-sm"
                                >
                                    Expected Date For Milestones
                                </Typography>
                                {/* Reference */}
                                <Typography
                                    className="ml-3 font-medium text-gray-800 px-3 py-2 rounded min-w-[300px] text-sm"
                                >
                                    Formula
                                </Typography>
                            </Grid>
                            
                            {dateCalculations.map((item, index) => (
                                <Grid
                                    key={`date_calc_${index}`}
                                    item xs={12}
                                    className="items-center w-full"
                                >
                                    <div className="flex items-center w-full gap-3">
                                        {/* Left Label */}
                                        <Typography
                                            className="text-gray-800 px-3 py-2 rounded min-w-[420px] text-sm"
                                        >
                                            {item.manualField && (
                                                <span className="text-[#0077B6] font-semibold block">
                                                    {item.manualField}
                                                </span>
                                            )}
                                            <span>{item.field}</span>
                                        </Typography>

                                        {/* = */}
                                        <span className="font-semibold item-center">=</span>

                                        {/* Reference */}
                                        <Typography
                                            className="text-gray-800 px-3 py-2 rounded min-w-[300px] text-sm"
                                        >
                                            {item.reference}
                                        </Typography>

                                        {!item?.noText && (
                                            <>
                                                <span className="font-semibold">+</span>

                                                {/* Input full width + suffix inside */}
                                                <div className="flex items-end gap-2 w-[160px] pb-2 bottom_only">
                                                    {/* Input with only bottom border */}
                                                    <FormikControl
                                                        formik={formik}
                                                        {...item}
                                                        className="text-sm rounded-none focus:border-blue-600 focus:ring-0 px-1 bottom_border_only"
                                                    />

                                                    {/* Days text beside input */}
                                                    <Typography className="text-black-700 text-base whitespace-nowrap">
                                                        {item.subLable}
                                                    </Typography>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="border border-gray-700 w-full"></div>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Box>

                {/* Fixed button at bottom */}
                <Box className="pt-3 mt-auto  bg-white">
                    <LoadingButton
                        loading={isLoading}
                        type="submit"
                        variant="contained"
                        className="w-full bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg text-white font-medium text-base shadow-md"
                        size="large"
                    >
                        {editData ? "Update Product" : "Add Product"}
                    </LoadingButton>
                </Box>
            </form>
        </Box>
    );
}

export default ProductCalculationsForm;